import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { OrdersTable } from './OrdersTable'
import { OrderDetailsModal } from '../modal/OrderDetailsModal'
import { NewIntakeOrderModal } from '../modal/NewIntakeOrderModal'
import { FilterSection } from '../filters/FilterSection'
import { DropdownProvider } from '../../../../shared/utils/DropdownContext'
import { useFilterPersistence } from '../../hooks/useFilterPersistence'
import { useOrdersData } from '../../hooks/useOrders'
import { useToast } from '../../../../shared/components/toast'
import { handleApiError } from '../../../../shared/utils/handleApiError'
import { useCategories } from '../../../../features/products/hooks/useMetaData'
import type { Order, OrdersParams } from '../../types/orders.types'
import { usePermissions, Permission } from '../../../../shared/hooks/usePermissions'
import { useAuthStore } from '../../../../features/auth/store'
import { useMerchants } from '../../../../features/merchants/hooks/useMerchants'

// Constants
const ITEMS_PER_PAGE = 20

// Main orders page with table, filters, search, and pagination
export function OrdersTableSection() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { hasPermission, role } = usePermissions()
  const { data: categoriesData } = useCategories()
  
  const { user } = useAuthStore()
  const isCollectiveStaff = user?.roleScope === 'COLLECTIVE'
  const { data: merchantsData } = useMerchants({ enabled: isCollectiveStaff })
  
  const merchantMap = useMemo(() => {
    if (!isCollectiveStaff || !merchantsData) return undefined
    return new Map<string, string>(merchantsData.map(m => [m.id, m.name]))
  }, [isCollectiveStaff, merchantsData])
  
  const categoryOptions = useMemo(() => [
    { title: 'All Categories', value: 'All Categories' },
    ...(categoriesData?.data || []).map(cat => ({ title: cat.name, value: cat.name }))
  ], [categoriesData])
  
  // Permission checks
  const canCreateOrder = hasPermission(Permission.ORDER_CREATE)
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isNewBespokeOrderModalOpen, setIsNewBespokeOrderModalOpen] = useState(false)
  const [isNewIntakeModalOpen, setIsNewIntakeModalOpen] = useState(false)

  // Endless scroll states
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [allOrders, setAllOrders] = useState<Order[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Filter state
  const { filters, updateFilters, clearFilters } = useFilterPersistence()
  
  // Build API parameters
  const apiParams: OrdersParams = useMemo(() => {
    const params: OrdersParams = {
      'page[size]': ITEMS_PER_PAGE,
    }

    // Add cursor for pagination
    if (cursor) {
      params['page[after]'] = cursor
    }

    // Add search term as item name filter
    if (searchTerm) {
      params['filter[items.itemName]'] = searchTerm
    }

    // Add status filter only (exclude payment filter)
    if (filters.status && filters.status !== 'All Status' ) {
      // Use status filter directly
      params['filter[status]'] = filters.status as OrdersParams['filter[status]']
    }

    // Add category filter only 
    if (filters.category && filters.category !== 'All Categories') {
      // Use status filter directly
      params['filter[category]'] = filters.category as OrdersParams['filter[category]']
    }

    if (filters.payment && filters.payment !== 'All Payments') {
      // Use status filter directly
      params['filter[status]'] = filters.payment as OrdersParams['filter[status]']
    }

    return params
  }, [cursor, searchTerm, filters.status, filters.payment, filters.category])

  // Data from API
  const { orders, meta, isLoading, error, refetch } = useOrdersData(apiParams)

  // Accumulate orders for infinite scroll
  useEffect(() => {
    if (orders && Array.isArray(orders)) {
      if (cursor) {
        // Append new orders (avoid duplicates)
        setAllOrders(prev => {
          const newOrders = orders.filter(newOrder =>
            !prev.some(existingOrder => existingOrder.id === newOrder.id)
          )
          return [...prev, ...newOrders]
        })
        setIsLoadingMore(false)
      } else {
        // Reset with new orders (first load or filter change)
        setAllOrders(orders)
      }
      setHasMore(meta?.hasNextPage === true)
    }
  }, [orders, cursor, isLoading, meta?.hasNextPage])

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore && meta?.endCursor) {
          setIsLoadingMore(true)
          setCursor(meta.endCursor)
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, isLoading, isLoadingMore, meta?.endCursor])

  // Apply client-side date filtering only
  const dateFilteredOrders = useMemo(() => {
    if (!allOrders) return []
    
    if (!filters.dateRange && (!filters.selectedDates || filters.selectedDates.length === 0)) {
      return allOrders
    }

    return allOrders.filter(order => {
      const orderDate = new Date(order.date)
      
      // Range filter
      if (filters.dateRange) {
        const start = new Date(filters.dateRange.start)
        const end = new Date(filters.dateRange.end)
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        return orderDate >= start && orderDate <= end
      }
      
      // Specific dates filter
      if (filters.selectedDates && filters.selectedDates.length > 0) {
        return filters.selectedDates.some(selectedDate => {
          const compareDate = new Date(selectedDate)
          compareDate.setHours(0, 0, 0, 0)
          const orderDateOnly = new Date(orderDate)
          orderDateOnly.setHours(0, 0, 0, 0)
          return orderDateOnly.getTime() === compareDate.getTime()
        })
      }
      
      return true
    })
  }, [allOrders, filters.dateRange, filters.selectedDates])

  // Error handling
  const handleError = useCallback(() => {
    if (error) {
      const errorMessage = handleApiError(error)
      toast.error('Failed to load orders', errorMessage)
    }
  }, [error, toast])

  // Show errors
  useEffect(() => {
    handleError()
  }, [handleError])

  const resetOrders = () => {
    setCursor(undefined)
    setHasMore(true)
    setIsLoadingMore(false)
  }

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm('')
    clearFilters()
    resetOrders()
  }, [clearFilters])

  const handleViewOrderDetails = useCallback((order: Order) => {
    setSelectedOrder(order)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedOrder(null)
  }, [])

  const handleOrderUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['orders'] })
    resetOrders()
    refetch()
  }, [queryClient, refetch])

  // Filter change handler
  const handleFilterChange = useCallback((newFilters: Partial<typeof filters>) => {
    updateFilters(newFilters)
    resetOrders()
  }, [updateFilters])

  return (
    <DropdownProvider>
      <div className="flex flex-col items-center min-h-screen py-8 px-4 lg:px-0">
        {/* Top Card - Search and Filters */}
        <div className="w-full max-w-[1052px] min-h-[181px] rounded-[10px] border-[1px] border-[#B8B5B6] bg-card mb-6 flex flex-col justify-center items-center gap-[16px] px-4 py-4 lg:py-0">
          {/* Header Section with Search and Actions */}
          <div className="w-full max-w-[997px] flex flex-col lg:flex-row justify-between items-stretch lg:items-center gap-3 lg:gap-0">
            <input
              type="text"
              placeholder="Search by product name..."
              value={searchTerm}
              onChange={(e) => {
                const sanitizedValue = e.target.value
                  .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
                  .replace(/[<>]/g, '')
                  .substring(0, 100)
                setSearchTerm(sanitizedValue)
                resetOrders()
              }}
              className="w-full lg:flex-1 lg:max-w-[743px] h-[46px] rounded-[10px] border-[1px] border-[#7C797C] pt-[12px] pr-[10px] pb-[12px] pl-[10px] outline-none"
              maxLength={100}
            />
            
            {/* Bespoke Order and On Behalf require ORDER_CREATE permission */}
            {canCreateOrder && (
              <div className="flex flex-col lg:flex-row gap-2">
                <button
                  onClick={() => setIsNewBespokeOrderModalOpen(true)}
                  className="w-full lg:w-[210px] h-[48px] rounded-[10px] pt-[12px] pr-[20px] pb-[12px] pl-[20px] flex items-center justify-center gap-[10px] border border-[#3F3E42] hover:bg-muted transition-colors"
                >
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 5V19" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.5 12H19.5" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span
                    className="text-foreground font-bold text-[16px]"
                    style={{ lineHeight: '140%', letterSpacing: '0%' }}
                  >
                    Bespoke Order
                  </span>
                </button>

                <button
                  onClick={() => setIsNewIntakeModalOpen(true)}
                  className="w-full lg:w-[210px] h-[48px] rounded-[10px] pt-[12px] pr-[20px] pb-[12px] pl-[20px] flex items-center justify-center gap-[10px] border border-[#3F3E42] hover:bg-muted transition-colors"
                >
                  <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12.5 5V19" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M5.5 12H19.5" stroke="#3F3E42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span
                    className="text-foreground font-bold text-[16px]"
                    style={{ lineHeight: '140%', letterSpacing: '0%' }}
                  >
                    On Behalf
                  </span>
                </button>
              </div>
            )}
          </div>

          {/* Filters Section - Now using FilterSection component */}
          <FilterSection
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearAllFilters}
            categoryOptions={categoryOptions}
          />
        </div>

        {/* Bottom Card - Orders Table */}
        <div className="w-full max-w-[1053px] max-h-fit rounded-[10px] border-[1px] border-[#B8B5B6] bg-card flex justify-center items-start pt-[44px] px-4 lg:px-0">
          {/* Table Content */}
          <div className="w-full max-w-[1009px] max-h-fit flex flex-col gap-[24px]">
            {/* Orders Count */}
            <div>
              <h2 
                className="font-bold text-[19px]"
                style={{
                  lineHeight: '140%',
                  letterSpacing: '0%',
                  background: 'linear-gradient(0deg, #3F3E42, #3F3E42), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}
              >
                Orders ({meta?.totalCount || 0})
              </h2>
            </div>

            {/* Table */}
            <div className="flex-1">
              <OrdersTable 
                data={dateFilteredOrders} 
                isLoading={isLoading && allOrders.length === 0} 
                onViewDetails={handleViewOrderDetails}
                merchantMap={merchantMap}
              />
            </div>

            {/* Infinite scroll sentinel + loading indicator */}
            <div ref={sentinelRef} className="flex justify-center py-8">
              {(isLoadingMore || (isLoading && allOrders.length > 0)) && (
                <div className="w-8 h-8 border-2 border-border border-t-gray-800 rounded-full animate-spin" />
              )}
            </div>
          </div>
        </div>

        {/* Order Details Modal */}
        <OrderDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          order={selectedOrder}
          onOrderUpdate={handleOrderUpdate}
          userRole={role}
        />

        {/* Bespoke Order — schema-driven CUSTOM_ORDER draft via POST /custom-orders (PE-1974).
            Replaces the retired NewBespokeOrderModal → POST /orders/bespoke (route never existed). */}
        <NewIntakeOrderModal
          isOpen={isNewBespokeOrderModalOpen}
          onClose={() => {
            setIsNewBespokeOrderModalOpen(false)
            queryClient.invalidateQueries({ queryKey: ['orders'] })
            refetch()
          }}
          lockDomain="CUSTOM_ORDER"
        />

        {/* Create on behalf (schema-driven: custom order / booking / package) */}
        <NewIntakeOrderModal
          isOpen={isNewIntakeModalOpen}
          onClose={() => setIsNewIntakeModalOpen(false)}
        />
      </div>
    </DropdownProvider>
  )
}